import { Link } from "react-router-dom";

import Seo from "../../components/Seo";
import { seoFromPublicPage } from "../../seo/publicPages";
import ArticleLayout from "./ArticleLayout";
import RelatedGuides from "./RelatedGuides";

export default function TrendDeductibilitate100Page() {
  return (
    <>
      <Seo {...seoFromPublicPage("/trend-deductibilitate-100-procente")} />
      <ArticleLayout
        eyebrow="Analiză fiscală"
        title="De ce tot mai multe firme trec de la deducere 50% la 100% în 2026"
        lead="Timp de ani, deducerea de 50% a fost alegerea «sigură» pentru multe firme: mai puțină birocrație, mai puțin risc. Dar contextul s-a schimbat. Costurile auto au crescut, taxele s-au înăsprit, iar instrumentele de conformitate au devenit accesibile. Diferența nu mai poate fi ignorată."
      >
        <p>
          Pentru firmele cu vehicule de până la 3,5 tone, regula implicită rămâne deducerea
          de 50% — atât pentru TVA, cât și pentru impozitul pe profit. E simplu: nu ai nevoie
          de foaie de parcurs, nu demonstrezi utilizare exclusivă, iar riscul la control e
          minimal.
        </p>
        <p>
          Dar «simplu» a devenit costisitor. Într-un context în care prețul combustibilului
          s-a stabilizat la niveluri ridicate, iar costurile cu service-ul, asigurările și
          piesele au crescut semnificativ, diferența dintre 50% și 100% nu mai e o sumă
          neglijabilă — mai ales pentru firmele cu mai multe vehicule.
        </p>

        <h2>Ce s-a schimbat în ultimii ani</h2>

        <h3>Costurile auto au crescut mai rapid decât inflația generală</h3>
        <p>
          Prețul combustibilului, deși fluctuant, s-a menținut semnificativ peste nivelurile
          de acum câțiva ani. La acestea se adaugă creșteri în lanț: anvelope, piese,
          manoperă, asigurări RCA (care au crescut vizibil) și CASCO. Un vehicul care
          genera cheltuieli de 1.200 lei/lună în 2022 generează astăzi 1.600–1.800 lei/lună
          pentru același tipar de utilizare.
        </p>

        <h3>Fiscalitatea s-a înăsprit</h3>
        <p>
          Modificările fiscale din ultimii ani au redus marjele și au crescut presiunea pe
          optimizarea legală. Firmele caută tot mai activ să recupereze tot ce li se cuvine
          — iar deducerea auto e un loc evident de unde «lași bani pe masă» dacă nu o
          valorifici integral.
        </p>

        <h3>Instrumentele de conformitate au devenit accesibile</h3>
        <p>
          Până recent, trecerea la 100% însemna foi de parcurs completate manual, verificări
          laborioase și risc de erori. Astăzi, aplicațiile dedicate automatizează
          completarea, validează coerența și reduc timpul necesar la câteva minute pe lună.
          Bariera practică a dispărut în mare parte.
        </p>

        <h2>Cât pierzi concret dacă rămâi la 50%</h2>
        <p>
          Exemplu pentru un autoturism cu cheltuieli lunare de 1.500 lei (fără TVA):
        </p>
        <div className="article-doc__table-wrap">
          <table className="article-doc__table">
            <thead>
              <tr>
                <th>Regim</th>
                <th>TVA recuperat (19%)</th>
                <th>Cheltuială deductibilă</th>
                <th>Economie impozit (16%)</th>
                <th>Beneficiu lunar total</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>50%</td>
                <td>142,50 lei</td>
                <td>750 lei</td>
                <td>120 lei</td>
                <td>~262,50 lei</td>
              </tr>
              <tr>
                <td>100%</td>
                <td>285 lei</td>
                <td>1.500 lei</td>
                <td>240 lei</td>
                <td>~525 lei</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>
          <strong>Diferența: ~262 lei/lună per vehicul.</strong> Pe un an, asta înseamnă
          peste 3.100 lei. Pentru o flotă de 3 mașini, aproape 10.000 lei/an rămân în firmă
          doar prin trecerea la 100%.
        </p>

        <h2>De ce «era mai simplu să stau la 50%» nu mai e un argument solid</h2>

        <h3>Timpul necesar s-a redus drastic</h3>
        <p>
          Odată cu automatizarea, foile de parcurs nu mai înseamnă ore pierdute în Excel
          sau completare manuală linie cu linie. Locațiile frecvente se reutilizează,
          alimentările se importă, iar coerența se verifică automat. Timpul lunar scade
          la 10–15 minute per vehicul.
        </p>

        <h3>Riscul de control nu e mai mare decât la 50%</h3>
        <p>
          Contrar percepției, un dosar coerent la 100% nu atrage mai mult atenția ANAF
          decât lipsa documentelor la 50%. Inspectorii verifică{" "}
          <Link to="/control-anaf-cheltuieli-autoturism">coerența globală</Link>: km vs.
          alimentări, scopuri vs. activitatea firmei, contracte și regulament intern.
          Un dosar complet și consistent e mai ușor de apărat decât unul care «evită
          întrebările».
        </p>

        <h3>Firmele din același domeniu deja au trecut</h3>
        <p>
          Competiția nu stă pe loc. Firmele care au trecut la 100% au costuri efective mai
          mici — ceea ce se reflectă în prețuri sau în marjă. A rămâne la 50% din comoditate
          devine un dezavantaj competitiv real.
        </p>

        <h2>Ce trebuie să existe pentru deducere 100%</h2>
        <p>
          Condițiile sunt clare și nu s-au schimbat:
        </p>
        <ul>
          <li>
            <strong>Foaie de parcurs</strong> completată lunar, cu cele 4 elemente
            obligatorii (categorie vehicul, km parcurși, scop și loc deplasare, normă
            proprie de consum). Detalii în{" "}
            <Link to="/ghid-foaie-parcurs-anaf">ghidul cerințelor ANAF</Link>.
          </li>
          <li>
            <strong>Regulament intern</strong> sau politică de utilizare care interzice
            folosirea în scop personal. Vezi{" "}
            <Link to="/regulament-intern-autoturism">articolul despre regulamentul intern</Link>.
          </li>
          <li>
            <strong>Bonuri și facturi</strong> care susțin cheltuielile deduse — alimentări,
            service, asigurări.
          </li>
          <li>
            <strong>Coerență</strong> între kilometri, consum, alimentări și rezervor.
          </li>
        </ul>

        <h2>Cine face deja trecerea</h2>

        <h3>PFA-uri cu un singur vehicul</h3>
        <p>
          Pentru un PFA care folosește mașina zilnic în activitate, diferența de 2.500–3.500
          lei/an e semnificativă. Foaia de parcurs e un document pe care oricum trebuie să-l
          poată prezenta la un eventual control — automatizarea o face și mai ușoară.
        </p>

        <h3>SRL-uri cu flotă mică (2–5 vehicule)</h3>
        <p>
          Aici impactul se multiplică. O firmă cu 4 mașini recuperează ~12.500 lei/an —
          echivalentul a 1–2 luni de cheltuieli auto. Investiția în conformitate (timp sau
          abonament la o aplicație) se amortizează rapid.
        </p>

        <h3>Firme cu agenți de teren, curieri sau echipe mobile</h3>
        <p>
          Unde deplasările sunt frecvente și justificate de activitatea economică, trecerea
          la 100% e aproape evidentă. Singura barieră era organizarea documentelor — și
          aceasta a căzut odată cu digitalizarea.
        </p>

        <h2>Ce se întâmplă dacă nu faci nimic</h2>
        <p>
          Rămâi la 50%. Nu e ilegal, nu e greșit. Dar asta înseamnă că:
        </p>
        <ul>
          <li>plătești efectiv mai mult decât ai putea, lună de lună;</li>
          <li>concurența care a trecut la 100% are un avantaj structural de cost;</li>
          <li>dacă vreodată vine un control, tot trebuie să arăți că mașina e folosită
              în activitate — chiar și la 50%, bonurile și contractele sunt verificate.</li>
        </ul>
        <div className="article-doc__callout article-doc__callout--warning">
          <strong>Lipsa foilor nu înseamnă lipsă de obligații</strong>
          <p>
            La 50% nu ai nevoie de foaie de parcurs pentru deducere. Dar dacă inspectorul
            constată că folosirea nu e nici măcar parțial economică, poate refuza și
            deducerea de 50%. Foaia de parcurs protejează, nu complică.
          </p>
        </div>

        <h2>Concluzie</h2>
        <p>
          Trecerea de la 50% la 100% nu mai e o decizie riscantă sau complicată. E o
          decizie economică, susținută de un dosar coerent și de instrumente care fac
          munca în locul tău. În contextul actual, diferența se simte direct în bani — iar
          banii rămân în firmă sau pleacă la stat.
        </p>
        <p>
          Dacă vrei să vezi cât ai de recuperat și cum arată procesul în practică, poți
          testa gratuit o aplicație care completează foile automat și validează coerența
          înainte să trimiți la contabil.
        </p>
        <RelatedGuides currentPath="/trend-deductibilitate-100-procente" />
      </ArticleLayout>
    </>
  );
}
